import React, {ReactNode} from 'react';
import {AnyObjectSchema} from 'yup';
import {QueryKey, UseQueryOptions, UseQueryResult} from '@tanstack/react-query';
import {Props as SelectProps, Theme as SelectTheme} from 'react-select';
import {
    IFormConfig,
    IFormConfigRedirectBehavior,
    IFormConfigSendRequestBehavior,
    IFormFieldConfig,
    IFormTabFieldConfig
} from '@src/@interfaces/components/Form/IFormConfig';
import {StringArray} from '@src/@interfaces/types';
import {UseFormProps, UseFormReturn} from 'react-hook-form';
import {EndpointUri} from '../EndpointUri';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {HTTPMethods} from '@src/@interfaces/http/HTTPMethods';
import {MakeOptional} from '../MakeOptional';
import {IFormFieldDesignContext} from '@src/@interfaces/contexts/IFormFieldDesignContext';
import {ControlledListProps} from './ControlledList';
import {HexColor} from '../colors';

export type FormProps<Model extends IEmptyModel> = {
    formName?: string,
    model?: Model,
    modelOptions?: Record<string, any>,
    refetchModel?: () => Promise<void>,
    config?: IFormConfig<Model>,
    customFields?: Record<string, FormCustomField<Model>>,
    customTabsContentWrappers?: Record<string | 'global', FormCustomTabContentWrapper<Model>>,
    customTabsControlWrappers?: Record<string | 'global', FormCustomTabControlWrapper<Model>>,
    customInitialValues?: Record<string, any>,
    context?: FormContext,
    controls?: ReactNode | ReactNode[],
    displayLoadingIndicator?: boolean,
    skipFormComponent?: boolean,
    formRendererComponent?: React.ComponentType<FormRendererProps<Model>>,
    formSubscriberComponent?: React.ComponentType<FormSubscriberProps<Model>>,
    formControlsComponent?: FormRendererProps<Model>['controlsComponent'],
}

export type FormSubscriberProps<Model extends IEmptyModel> = Pick<
    FormProps<Model>,
    'formName' | 'model' | 'modelOptions' | 'refetchModel' | 'config'
> & { children: ReactNode };

export type FormCustomTabContentWrapperProps<Model extends IEmptyModel> = Pick<
    FormRendererProps<Model>,
    'formName' |
    'context' |
    'model' |
    'modelOptions'
> & {
    children: React.ReactNode,
};

export type FormCustomTabControlWrapperProps<Model extends IEmptyModel> = FormCustomTabContentWrapperProps<Model>;

export type FormCustomTabContentWrapper<Model extends IEmptyModel> = React.ComponentType<
    FormCustomTabContentWrapperProps<Model>
>;

export type FormCustomTabControlWrapper<Model extends IEmptyModel> = React.ComponentType<
    FormCustomTabControlWrapperProps<Model>
>;

export type FormCustomField<Model extends IEmptyModel = IEmptyModel> = {
    config?: IFormFieldConfig,
    component: React.ComponentType<FormAbstractFieldProps<Model, FormFieldConfigType>>
};

export type FormViewFromProps<Model extends IEmptyModel> = ControlledFormProps<Model> & { isReadOnly?: boolean };
export type ControlledFormProps<Model extends IEmptyModel> = FormProps<Model>;

export type FormState = {
    error?: Error,
    loading?: boolean,
    displayOverlay?: boolean,
};

export type Fields<Model, FieldConfig> = StringArray<FieldConfig> | {
    // eslint-disable-next-line @typescript-eslint/ban-types
    [K in keyof Model]: Model[K] extends Function ? never : FieldConfig;
}

export enum FormFieldConfigSize {
    NORMAL = '',
    SM = 'sm',
    LG = 'lg'
}

export enum FormFieldWidgets {
    COLOR_PICKER = 'color_picker',
    DATE_PICKER = 'date_picker',
    FILE_UPLOAD = 'file_upload',
    NESTED_FORM = 'nested_form',
    NESTED_LIST = 'nested_list',
    RELATION = 'relation',
    REPEATER = 'repeater',
}

export enum FormFieldAvailableTypes {
    CUSTOM = 'custom',
    TEXT = 'text',
    NUMBER = 'number',
    PRICE = 'price',
    PASSWORD = 'password',
    EMAIL = 'email',
    TEXTAREA = 'textarea',
    SELECT = 'dropdown',
    RADIO_LIST = 'radio_list',
    CHECKBOX = 'checkbox',
    CHECKBOX_LIST = 'checkbox_list',
    SWITCH = 'switch',
}

export type FormFieldConfigType = FormFieldAvailableTypes | FormFieldWidgets;

export type FormContext = FormContextEnum | string;

export enum FormContextEnum {
    ANY = 'any',
    CREATE = 'create',
    UPDATE = 'update',
}

export enum FormFieldTriggerAction {
    SHOW = 'show',
    HIDE = 'hide',
    ENABLE = 'enable',
    DISABLE = 'disable',
    EMPTY = 'empty',
}

export enum FormFieldTriggerConditionEnum {
    CHECKED = 'checked',
    UNCHECKED = 'unchecked',
}

export type FormFieldTriggerCondition = FormFieldTriggerConditionEnum;

export enum FormFieldValidationRules {
    REQUIRED = 'required',
    NULLABLE = 'nullable',
    MIN = 'min',
    MAX = 'max',
}

export type FormFieldSelectOptions = string | string[] | number[] | Record<string | number, string>;

export type FormRendererProps<Model extends IEmptyModel> = Pick<
    FormProps<Model>,
    'customFields' |
    'customTabsContentWrappers' |
    'customTabsControlWrappers' |
    'formName' |
    'model' |
    'modelOptions' |
    'controls' |
    'context'
> & Pick<Required<FormProps<Model>>, 'config'> & {
    fieldsComponent?: React.ComponentType<FormFieldsProps<Model>>,
    controlsComponent?: React.ComponentType<FormControlsProps<Model>>,
    formFieldDesignContextOptions?: Partial<IFormFieldDesignContext>,
    error?: Error,
    initialValues?: Record<string, any>,
    yupScheme?: AnyObjectSchema,
    formProps?: UseFormProps,
    onSubmitSuccess: (data: Record<string, any>, e?: React.BaseSyntheticEvent) => void | Promise<void>,
    onSubmitFail?: (errors: Record<string, any>, e?: React.BaseSyntheticEvent) => void | Promise<void>,
    onSubmitRequestSuccess?: (responseData: any, methods: UseFormReturn) => any | Promise<any>,
    onSubmitRequestFail?: (error: Error, methods: UseFormReturn) => any | Promise<any>,
    skipFormComponent?: boolean;
}

export type FormControlsProps<Model extends IEmptyModel> = { formName?: string } & IFormConfig<Model>;

export type FormFieldsProps<Model extends IEmptyModel> = Pick<
    FormRendererProps<Model>,
    'formName' |
    'config' |
    'context' |
    'model' |
    'modelOptions' |
    'customFields' |
    'customTabsContentWrappers' |
    'customTabsControlWrappers'
>;

export type FormSwitchFieldProps<Model extends IEmptyModel> = FormFieldProps<Model>;

export type SelectFieldProps<Model extends IEmptyModel> = FormFieldProps<Model> & {
    valueResolver?: (value: any | null, options: FormFieldOption[]) => FormFieldOption | FormFieldOption[] | null | undefined
    customTheme?: (theme: SelectTheme) => SelectTheme,
    customStyles?: SelectProps['styles'],
};

export type SelectFieldStylesBag<K extends string = 'default' | 'light' | 'invalid' | 'dark' | 'sm' | 'lg'> = {
    [P in K]: NonNullable<SelectProps['styles']>
}

export type FormFieldOption<TVal = string | number | null> = {
    label: string,
    value: TVal,
    marker?: HexColor,
    avatar_public_path?: string,
    icon_public_path?: string,
};

export type FormAbstractFieldProps<Model extends IEmptyModel, TType extends string> =
    MakeOptional<FormFieldProps<Model>, 'id' | 'label'> & { type: TType };

export type FormFieldProps<Model extends IEmptyModel> = {
    id: string,
    label: string,
    modelOptions?: Record<string, FormFieldOption[]>,
    model?: Model,
    formName?: string,
    fieldName: string | Exclude<keyof Model, symbol | number | boolean>,
    fieldLabel: string,
    config: IFormFieldConfig | IFormTabFieldConfig,
    labelL10NPrefix?: string | null,
    className?: string,
    forceReadonly?: boolean,
    forceDisabled?: boolean,
    forceValue?: any,
    tabName?: string | number,
}

export type NestedListWidgetProps<Model extends IEmptyModel> = FormFieldProps<Model> & {
    controlledListComponent?: React.ComponentType<ControlledListProps<Model>>,
};

export type FormConfigBehavior = IFormConfigRedirectBehavior | IFormConfigSendRequestBehavior;

export type FormConfigUrl = EndpointUri;

export type FormModelProviderProps<Model extends IEmptyModel = IEmptyModel> = {
    requestUri: string,
    requestMethod?: HTTPMethods,
    requestBody: Record<string, any>,
    query: {
        queryKey: QueryKey,
        options?: Omit<UseQueryOptions<FormModelHttpResponse<Model>, Error>, 'queryKey'>,
    },
    children: (queryResult: UseQueryResult<FormModelHttpResponse<Model>, Error>) => React.ReactNode
}

export type FormModelHttpResponse<Model extends IEmptyModel = IEmptyModel> = {
    model: Model,
    options?: Record<string, any>
}

type FileUploadWidgetFormFieldConfig<Model extends IEmptyModel, Mode extends 'file' | 'image' = 'file'> =
    FormFieldProps<Model>['config'] &
    {
        multiple?: boolean,
        mode: 'file' | 'image'
        fileTypes?: string,
        mimeTypes?: string,
        maxFilesize?: number, // in MB
        maxFiles?: number
    } &
    (
        Mode extends 'image' ?
            {
                imageWidth?: number,
                imageHeight?: number,
                thumbOptions?: {
                    quality?: number
                }
            } :
            Record<string, never>
        );

export type FileUploadWidgetFormFieldProps<Model extends IEmptyModel, Mode extends 'file' | 'image' = 'file'> =
    FormFieldProps<Model> &
    {
        config: FileUploadWidgetFormFieldConfig<Model, Mode>,
    };
